import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaEye, FaSearch, FaDollarSign, FaClock, FaChartLine, FaCalendarAlt, FaChevronDown, FaChevronRight, FaTrash } from 'react-icons/fa';
import { fetchAPI, API_BASE_URL } from '../../utils/api';
import { showSuccess, showError } from '../../utils/toastUtils';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmDialog from '../../components/common/ConfirmDialog';

export default function AdminPayments() {
    const { confirm, ConfirmDialog: confirmDialogProps } = useConfirm();
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedGrades, setExpandedGrades] = useState({});
    const [showMonthSummary, setShowMonthSummary] = useState(true);

    const loadPayments = async (status = 'all') => {
        try {
            setLoading(true);
            const endpoint = `/admin/get_all_payments.php${status !== 'all' ? `?status=${status}` : ''}`;
            const data = await fetchAPI(endpoint);
            setPayments(data.payments);
            setSummary(data.summary);
        } catch (err) {
            console.error("Failed to load payments", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPayments(statusFilter);
    }, [statusFilter]);

    const handleAction = async (id, status) => {
        const confirmed = await confirm({
            title: `${status} Payment`,
            message: `Are you sure you want to ${status.toLowerCase()} this payment?`,
            confirmText: status,
            cancelText: 'Cancel',
            danger: status === 'Rejected',
            icon: status === 'Approved' ? 'check' : 'warning'
        });

        if (!confirmed) return;

        try {
            await fetchAPI('/admin/update_payment_status.php', {
                method: 'POST',
                body: JSON.stringify({ id, status })
            });
            showSuccess(`Payment ${status.toLowerCase()}ed`);
            loadPayments(statusFilter);
        } catch (err) {
            showError("Error: " + err.message);
        }
    };

    const handleDelete = async (id) => {
        const confirmed = await confirm({
            title: 'Delete Payment',
            message: 'Are you sure you want to delete this payment? This action cannot be undone.',
            confirmText: 'Delete Payment',
            cancelText: 'Cancel',
            danger: true,
            icon: 'trash'
        });

        if (!confirmed) return;

        try {
            await fetchAPI('/admin/delete_payment.php', {
                method: 'POST',
                body: JSON.stringify({ id })
            });
            showSuccess('Payment deleted successfully');
            loadPayments(statusFilter);
        } catch (err) {
            showError("Error: " + err.message);
        }
    };

    const filteredPayments = payments.filter(p =>
        (p.student_name?.toLowerCase() || '').includes(filter.toLowerCase()) ||
        (p.class_name?.toLowerCase() || '').includes(filter.toLowerCase()) ||
        (p.reference_no?.toLowerCase() || '').includes(filter.toLowerCase()) ||
        (p.month?.toLowerCase() || '').includes(filter.toLowerCase())
    );

    const getStatusColor = (status) => {
        if (status === 'Approved') return 'bg-green-100 text-green-700';
        if (status === 'Pending') return 'bg-yellow-100 text-yellow-700';
        if (status === 'Rejected') return 'bg-red-100 text-red-700';
        return 'bg-gray-100 text-gray-700';
    };

    const toggleGradeExpand = (grade) => {
        setExpandedGrades(prev => ({
            ...prev,
            [grade]: !prev[grade]
        }));
    };

    if (loading && !summary) return <div className="p-8 text-center text-gray-500 font-bold">Loading payments...</div>;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/60 backdrop-blur-xl rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2">
                        Payment Management
                    </h1>
                    <p className="text-gray-500 font-medium">
                        Verify student payments and track revenue.
                    </p>
                </div>
            </div>
            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                            <FaDollarSign className="text-3xl opacity-80" />
                            <span className="text-xs font-black uppercase tracking-wider opacity-80">Total Revenue</span>
                        </div>
                        <div className="text-3xl font-bold mb-1">LKR {summary.total_revenue.toLocaleString()}</div>
                        <div className="text-xs opacity-80">{summary.approved_count} approved payments</div>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                            <FaClock className="text-3xl opacity-80" />
                            <span className="text-xs font-black uppercase tracking-wider opacity-80">Pending</span>
                        </div>
                        <div className="text-3xl font-bold mb-1">LKR {summary.pending_amount.toLocaleString()}</div>
                        <div className="text-xs opacity-80">{summary.pending_count} pending payments</div>
                    </div>

                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                            <FaChartLine className="text-3xl opacity-80" />
                            <span className="text-xs font-black uppercase tracking-wider opacity-80">Approval Rate</span>
                        </div>
                        <div className="text-3xl font-bold mb-1">{summary.approval_rate}%</div>
                        <div className="text-xs opacity-80">{summary.rejected_count} rejected</div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                            <FaCalendarAlt className="text-3xl opacity-80" />
                            <span className="text-xs font-black uppercase tracking-wider opacity-80">This Month</span>
                        </div>
                        <div className="text-3xl font-bold mb-1">LKR {summary.this_month_revenue.toLocaleString()}</div>
                        <div className="text-xs opacity-80">Current month revenue</div>
                    </div>
                </div>
            )}

            {/* Month-wise Summary */}
            {summary && summary.monthly_stats && summary.monthly_stats.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div
                        className="px-6 py-4 border-b border-gray-100 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => setShowMonthSummary(!showMonthSummary)}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-2">
                                    {showMonthSummary ? (
                                        <FaChevronDown className="text-gray-400 text-sm" />
                                    ) : (
                                        <FaChevronRight className="text-gray-400 text-sm" />
                                    )}
                                    <h3 className="text-lg font-bold text-gray-900">Month-wise Payment Summary</h3>
                                </div>
                                <p className="text-xs text-gray-500 mt-1 ml-6">Revenue and payment breakdown by course month</p>
                            </div>
                        </div>
                    </div>
                    {showMonthSummary && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-100">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Month</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Revenue</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Total Payments</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Approved</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Pending</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Rejected</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-50">
                                    {summary.monthly_stats.map((stat, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-gray-900">{stat.month}</div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="text-sm font-bold text-green-600">LKR {stat.total_revenue.toLocaleString()}</div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="text-sm font-bold text-gray-900">{stat.total_payments}</div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                                                    {stat.approved}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold">
                                                    {stat.pending}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                                                    {stat.rejected}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Grade-wise Summary with Month Breakdown */}
            {summary && summary.grade_stats && summary.grade_stats.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                        <h3 className="text-lg font-bold text-gray-900">Grade-wise Payment Summary</h3>
                        <p className="text-xs text-gray-500 mt-1">Revenue and payment breakdown by grade level with month-wise details</p>
                    </div>
                    <div className="overflow-x-auto">
                        {summary.grade_stats.map((stat, idx) => (
                            <div key={idx} className="border-b border-gray-100 last:border-b-0">
                                {/* Grade Summary Row - Clickable */}
                                <div
                                    className="bg-gray-50 px-6 py-4 hover:bg-gray-100 transition-colors cursor-pointer"
                                    onClick={() => toggleGradeExpand(stat.grade)}
                                >
                                    <div className="grid grid-cols-6 gap-4 items-center">
                                        <div className="col-span-1 flex items-center gap-2">
                                            {expandedGrades[stat.grade] ? (
                                                <FaChevronDown className="text-gray-400 text-sm" />
                                            ) : (
                                                <FaChevronRight className="text-gray-400 text-sm" />
                                            )}
                                            <div className="font-bold text-gray-900 text-lg">Grade {stat.grade}</div>
                                        </div>
                                        <div className="col-span-1 text-right">
                                            <div className="text-sm font-bold text-green-600">LKR {stat.total_revenue.toLocaleString()}</div>
                                            <div className="text-xs text-gray-400">Total Revenue</div>
                                        </div>
                                        <div className="col-span-1 text-right">
                                            <div className="text-sm font-bold text-gray-900">{stat.total_payments}</div>
                                            <div className="text-xs text-gray-400">Total Payments</div>
                                        </div>
                                        <div className="col-span-1 text-right">
                                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                                                {stat.approved} Approved
                                            </span>
                                        </div>
                                        <div className="col-span-1 text-right">
                                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold">
                                                {stat.pending} Pending
                                            </span>
                                        </div>
                                        <div className="col-span-1 text-right">
                                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                                                {stat.rejected} Rejected
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Month Breakdown - Only show when expanded */}
                                {expandedGrades[stat.grade] && stat.months && stat.months.length > 0 && (
                                    <div className="bg-white">
                                        <table className="min-w-full">
                                            <thead>
                                                <tr className="bg-gray-50 border-t border-gray-100">
                                                    <th className="px-6 py-2 text-left text-xs font-bold text-gray-400 uppercase tracking-widest pl-12">Month</th>
                                                    <th className="px-6 py-2 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Revenue</th>
                                                    <th className="px-6 py-2 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Payments</th>
                                                    <th className="px-6 py-2 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Approved</th>
                                                    <th className="px-6 py-2 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Pending</th>
                                                    <th className="px-6 py-2 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Rejected</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stat.months.map((monthStat, mIdx) => (
                                                    <tr key={mIdx} className="hover:bg-gray-50/50 transition-colors border-t border-gray-50">
                                                        <td className="px-6 py-3 pl-12">
                                                            <div className="text-sm font-medium text-gray-700">→ {monthStat.month}</div>
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <div className="text-sm font-bold text-green-600">LKR {monthStat.revenue.toLocaleString()}</div>
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <div className="text-sm text-gray-700">{monthStat.payments}</div>
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <span className="text-xs text-green-700">{monthStat.approved}</span>
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <span className="text-xs text-yellow-700">{monthStat.pending}</span>
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <span className="text-xs text-red-700">{monthStat.rejected}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters and Search */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex gap-2">
                    {['all', 'Pending', 'Approved', 'Rejected'].map((status) => (
                        <button
                            key={status}
                            onClick={() => setStatusFilter(status)}
                            className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${statusFilter === status
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                                }`}
                        >
                            {status === 'all' ? 'All Payments' : status}
                        </button>
                    ))}
                </div>
                <div className="relative w-full md:w-80">
                    <input
                        type="text"
                        placeholder="Search student, class, or month..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                    />
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
            </div>

            {/* Payments Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Student / Class</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Month</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Amount / Ref</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Slip</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Date</th>
                                <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-50">
                            {filteredPayments.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-400 font-medium italic">No payments found</td>
                                </tr>
                            ) : filteredPayments.map((p) => (
                                <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-gray-900">{p.student_name}</div>
                                        <div className="text-xs text-gray-400 font-bold uppercase mt-0.5">{p.index_number}</div>
                                        <div className="text-xs text-blue-600 font-black uppercase mt-1">
                                            {p.class_name} - Grade {p.grade}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-bold text-gray-900">{p.month}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-bold text-gray-900">LKR {p.amount}</div>
                                        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ref: {p.reference_no}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${getStatusColor(p.status)}`}>
                                            {p.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <button
                                            onClick={() => window.open(`${API_BASE_URL}/${p.slip_url}`, '_blank')}
                                            className="flex items-center gap-2 text-xs font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg transition-all"
                                        >
                                            <FaEye /> VIEW
                                        </button>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-500 font-medium">{new Date(p.created_at).toLocaleDateString()}</div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            {p.status === 'Pending' && (
                                                <>
                                                    <button
                                                        onClick={() => handleAction(p.id, 'Approved')}
                                                        className="w-10 h-10 bg-green-50 text-green-600 rounded-xl flex items-center justify-center hover:bg-green-600 hover:text-white transition-all shadow-sm"
                                                        title="Approve"
                                                    >
                                                        <FaCheck />
                                                    </button>
                                                    <button
                                                        onClick={() => handleAction(p.id, 'Rejected')}
                                                        className="w-10 h-10 bg-red-50 text-red-600 rounded-xl flex items-center justify-center hover:bg-red-600 hover:text-white transition-all shadow-sm"
                                                        title="Reject"
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </>
                                            )}
                                            <button
                                                onClick={() => handleDelete(p.id)}
                                                className="w-10 h-10 bg-gray-50 text-gray-600 rounded-xl flex items-center justify-center hover:bg-red-600 hover:text-white transition-all shadow-sm"
                                                title="Delete Payment"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Confirmation Dialog */}
            {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
        </div>
    );
}
